/*
 * 戒导航 · 下载中心 —— 通用「文件管理器」表格（P7d）
 * =============================================================================
 * 设计依据：doc/知识库与文件下载中心设计方案.md §7.2（P7.3 列规范 / P7.7 扩展性 /
 *   P7.8 组件架构 / P7.9 响应式）。
 *
 * 定位：一个**纯展示**的表格组件，被下载中心首页「最新资料」、分类页「资料列表」、
 *   详情页「文件清单」三处复用（三者列结构不同，靠 columns 参数区分）。
 *   · 不查库、不判权限、不拼 URL 语义 —— 数据与权限由调用方（DownloadPage）传入；
 *   · 只负责：表头（含排序按钮）/ 行渲染 / 密度 / 响应式列显隐 / 加载骨架屏 / 空态。
 *
 * ★ 扩展性（用户明确要求「要考虑扩展」）
 *   · 列可插拔：列 = 一条 `TableColumn` def（key/labelKey/width/align/priority/sortKey/render），
 *     新增列（版本/作者/分类/评分…）**只加一条 def**，本组件无需改动。
 *   · 排序白名单：可排序列须 `sortKey` 命中 `sortable`（来自后端 meta.sortable），
 *     前后端单一契约，避免排序键漂移。
 *   · 类型映射：图标/色调/文案走 utils/fileTypes.ts 的注册表。
 *   · 列级权限：`TableColumn.visibleFor?(actor)` —— 返回 false 的列**整体不渲染**
 *     （表头 + 单元格同时消失），供未来「管理列仅编辑者可见」等场景零改动接入。
 *
 * ★ 响应式（P7.9）：用列 def 的 `priority` 生成 `jd-col--p{N}` 类，配合 less/forum.less
 *   的媒体查询按优先级隐藏次要列（不换行、不堆叠，保持表格语义）。
 *   优先级：1=名称/操作（始终显示）；2=大小·下载·浏览（核心数值）；3=时间等次要列。
 *
 * ★ 无障碍：可排序表头是真 `<button>` + `aria-sort`；可点击行带 tabindex 与
 *   Enter 键支持；装饰性图标 `aria-hidden`。
 *
 * ★ 安全：行点击导航前会判断事件源是否落在交互元素（a/button）上，
 *   避免「点行内下载按钮又触发整行跳转」的双重动作。
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Icon from 'flarum/common/components/Icon';

/** 列定义（扩展点：新增列只加一条）。 */

/** 组件入参。 */

export default class ResourceTable extends Component {
  view() {
    const attrs = this.attrs;
    const columns = this.visibleColumns(attrs.columns);
    const loading = !!attrs.loading;
    const items = attrs.items ?? [];
    const density = attrs.density ?? 'comfortable';
    if (loading) {
      return this.skeletonView(columns, density, attrs.skeletonRows ?? 8);
    }
    if (!items.length) {
      return m("div", {
        className: 'jd-table-wrap jd-density-' + density
      }, attrs.emptyView ?? m("div", {
        className: "jd-empty"
      }, m(Icon, {
        name: "far fa-folder-open"
      }), m("p", null, app.translator.trans('jiedh-downloads.forum.empty_list'))));
    }
    return m("div", {
      className: 'jd-table-wrap jd-density-' + density
    }, m("table", {
      className: "jd-table"
    }, this.headView(columns, attrs), m("tbody", {
      className: "jd-tbody"
    }, items.map((item, index) => this.rowView(item, index, columns, attrs)))));
  }

  /* ------------------------------------------------------------------
   * 列过滤（列级权限）
   * ------------------------------------------------------------------ */

  visibleColumns(columns) {
    const actor = app.session.user;
    return (columns ?? []).filter(col => !col.visibleFor || col.visibleFor(actor));
  }

  /* ------------------------------------------------------------------
   * 表头
   * ------------------------------------------------------------------ */

  headView(columns, attrs) {
    const {
      sort,
      dir,
      sortable = [],
      onSort
    } = attrs;
    return m("thead", {
      className: "jd-thead"
    }, m("tr", null, columns.map(col => {
      const canSort = !!col.sortKey && !!onSort && sortable.indexOf(col.sortKey) !== -1;
      const active = canSort && sort === col.sortKey;
      const ariaSort = active ? dir === 'asc' ? 'ascending' : 'descending' : canSort ? 'none' : undefined;
      const cls = this.colClass('jd-th', col, 'left');
      if (!canSort) {
        return m("th", {
          className: cls,
          style: col.width ? {
            width: col.width
          } : undefined,
          key: col.key
        }, this.headLabel(col));
      }
      return m("th", {
        className: cls + ' is-sortable',
        style: col.width ? {
          width: col.width
        } : undefined,
        "aria-sort": ariaSort,
        key: col.key
      }, m("button", {
        type: "button",
        className: "jd-th-btn",
        onclick: () => onSort(col.sortKey)
      }, m("span", {
        className: "jd-th-label"
      }, this.headLabel(col)), m(Icon, {
        name: active ? dir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down' : 'fas fa-sort'
      })));
    })));
  }

  /** 表头文字：自定义渲染优先，其次按 labelKey 取语言包。 */
  headLabel(col) {
    if (col.headerRender) return col.headerRender();
    if (col.labelKey) return app.translator.trans('jiedh-downloads.forum.' + col.labelKey);
    return null;
  }

  /* ------------------------------------------------------------------
   * 数据行
   * ------------------------------------------------------------------ */

  rowView(item, index, columns, attrs) {
    const href = attrs.rowHref ? attrs.rowHref(item) : null;
    return m("tr", {
      className: 'jd-row' + (href ? ' is-clickable' : ''),
      key: attrs.rowKey(item, index),
      tabindex: href ? 0 : undefined,
      onclick: e => this.handleRowActivate(e, href),
      onkeydown: e => {
        // 键盘可达：聚焦行后回车进入详情（避开行内交互元素的回车）
        if (e.key === 'Enter') this.handleRowActivate(e, href);
      }
    }, columns.map(col => m("td", {
      className: this.colClass('jd-td', col),
      key: col.key
    }, col.render(item))));
  }

  /**
   * 整行激活（点击 / 回车）。
   * 若事件源落在行内交互元素（a/button）上则不触发整行跳转，避免双重动作。
   */
  handleRowActivate(e, href) {
    if (!href) return;
    const target = e?.target;
    if (target instanceof Element && target.closest('a, button')) return;
    m.route.set(href);
  }

  /* ------------------------------------------------------------------
   * 骨架屏（保留表头，正文灰条；避免加载时布局跳动）
   * ------------------------------------------------------------------ */

  skeletonView(columns, density, rows) {
    return m("div", {
      className: 'jd-table-wrap jd-density-' + density + ' is-loading'
    }, m("table", {
      className: "jd-table"
    }, this.headView(columns, {
      sortable: [],
      rowKey: () => '',
      items: [],
      columns
    }), m("tbody", {
      className: "jd-tbody"
    }, Array.from({
      length: rows
    }).map((_, i) => m("tr", {
      className: "jd-row is-skeleton",
      key: i
    }, columns.map(col => m("td", {
      className: this.colClass('jd-td', col),
      key: col.key
    }, m("span", {
      className: "jd-skel-bar",
      style: {
        width: col.key === 'name' ? '58%' : '40px'
      }
    }))))))));
  }

  /* ------------------------------------------------------------------
   * 工具
   * ------------------------------------------------------------------ */

  /** 单元格 / 表头类名（列键 + 对齐 + 响应式优先级）。 */
  colClass(base, col, defaultAlign) {
    if (defaultAlign === void 0) {
      defaultAlign = 'left';
    }
    return [base, 'jd-col--' + col.key, 'jd-align--' + (col.align ?? defaultAlign), 'jd-col--p' + (col.priority ?? 1)].join(' ');
  }
}
flarum.reg.add('jiedh-downloads', 'forum/components/ResourceTable', ResourceTable);